import React, { useRef, useState } from "react";
import {
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CancelIcon from "@mui/icons-material/Cancel";

const FILTER_WIDTH = 140;

export default function TableFilter({ value, setValue, data, filterName }) {
  const [open, setOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(FILTER_WIDTH);
  const formRef = useRef(null);

  const hasValue = value.length > 0;
  const labelShrink = open || hasValue;

  return (
    <FormControl
      ref={formRef}
      variant="outlined"
      size="small"
      className="table-filter"
      sx={{
        width: { xs: "100%", sm: FILTER_WIDTH },
        minWidth: { xs: "100%", sm: FILTER_WIDTH },
        flexShrink: 0,
        "& .MuiOutlinedInput-root": {
          width: "100%",
        },
        "& .MuiSelect-select": {
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          minHeight: "1.4375em",
        },
      }}
    >
      <InputLabel id="table-filter-label" shrink={labelShrink}>
        {filterName}
      </InputLabel>
      <Select
        labelId="table-filter-label"
        multiple
        value={value}
        label={filterName}
        input={<OutlinedInput label={filterName} notched={labelShrink} />}
        onChange={(e) => setValue([...e.target.value])}
        IconComponent={KeyboardArrowDownIcon}
        onOpen={() => {
          setOpen(true);
          if (formRef.current) {
            setMenuWidth(formRef.current.offsetWidth);
          }
        }}
        onClose={() => setOpen(false)}
        MenuProps={{
          disableScrollLock: true,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          PaperProps: {
            sx: {
              minWidth: Math.max(menuWidth || FILTER_WIDTH, 200),
              width: "max-content",
              maxWidth: 320,
              maxHeight: 320,
              boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
              borderRadius: "8px",
              mt: 0.5,
              "& .MuiMenuItem-root": {
                whiteSpace: "nowrap",
                py: 0.75,
                px: 1.5,
              },
            },
          },
        }}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return null;
          }

          if (selected.length === 1) {
            const selectedItem = data.find((item) => item.value == selected[0]);
            return (
              <Chip
                size="small"
                label={selectedItem?.label || selected[0]}
                clickable
                sx={{
                  maxWidth: "100%",
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
                deleteIcon={
                  <CancelIcon
                    onMouseDown={(event) => {
                      event.stopPropagation();
                      setValue([]);
                      setOpen(false);
                    }}
                  />
                }
                onDelete={() => {
                  setValue([]);
                  setOpen(false);
                }}
              />
            );
          }

          const firstItem = data.find((item) => item.value == selected[0]);
          const additionalCount = selected.length - 1;
          return (
            <Chip
              size="small"
              label={`${firstItem?.label || selected[0]}, +${additionalCount}`}
              clickable
              sx={{
                maxWidth: "100%",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
              deleteIcon={
                <CancelIcon
                  onMouseDown={(event) => {
                    event.stopPropagation();
                    setValue([]);
                    setOpen(false);
                  }}
                />
              }
              onDelete={() => {
                setValue([]);
                setOpen(false);
              }}
            />
          );
        }}
      >
        {data.map((item) => (
          <MenuItem key={item.value} value={item.value} sx={{ py: 0.75 }}>
            <Checkbox
              size="small"
              checked={value.includes(item.value)}
              sx={{ p: 0.5, mr: 1 }}
            />
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
